import React, { useState, useRef, useEffect } from 'react';

/**
 * Custom styled select dropdown component
 * @param {Object} props
 * @param {string} props.value - Current selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Array} props.options - Array of options { id, name, value? }
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.hasError - Whether to show error styling
 * @param {string} props.errorMessage - Error message to display
 * @param {string} props.label - Label text
 * @param {boolean} props.required - Whether field is required
 * @param {boolean} props.disabled - Whether dropdown is disabled
 */
export default function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  hasError = false,
  errorMessage = '',
  label = '',
  required = false,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(
    (opt) => (opt.value || opt.name || opt.id) === value
  );
  // If no matching option found, display the value itself (useful when loading existing data)
  const displayValue = selectedOption
    ? selectedOption.name || selectedOption.value || selectedOption.id
    : value || '';

  const handleSelect = (option) => {
    const optionValue = option.value || option.name || option.id;
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '0.75rem',
          }}
        >
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}

      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={{
            width: '100%',
            padding: '1rem 2.5rem 1rem 1rem',
            fontSize: '1rem',
            textAlign: 'left',
            border: hasError ? '1px solid #ef4444' : '1px solid #d1d5db',
            borderRadius: '0.5rem',
            outline: 'none',
            backgroundColor: disabled ? '#f9fafb' : '#ffffff',
            color: displayValue ? '#111827' : '#9ca3af',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          onFocus={(e) => {
            if (!disabled && !hasError) {
              e.target.style.borderColor = '#2563eb';
            }
          }}
          onBlur={(e) => {
            if (!hasError) {
              e.target.style.borderColor = '#d1d5db';
            }
          }}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayValue || placeholder}
          </span>
          <svg
            style={{
              width: '1.25rem',
              height: '1.25rem',
              color: '#6b7280',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              flexShrink: 0,
              marginLeft: '0.5rem',
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isOpen && !disabled && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.25rem',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              zIndex: 1000,
              maxHeight: '16rem',
              overflowY: 'auto',
            }}
          >
            {options.length === 0 ? (
              <div
                style={{
                  padding: '0.75rem',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                }}
              >
                No options available
              </div>
            ) : (
              options.map((option, index) => {
                const optionValue = option.value || option.name || option.id;
                const isSelected = value === optionValue;

                return (
                  <button
                    key={option.id || index}
                    type="button"
                    onClick={() => handleSelect(option)}
                    style={{
                    width: '100%',
                    padding: '1rem',
                    textAlign: 'left',
                    fontSize: '1rem',
                      color: isSelected ? '#2563eb' : '#111827',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span>{option.name || option.value || option.id}</span>
                    {isSelected && (
                      <svg
                        style={{
                          width: '1rem',
                          height: '1rem',
                          color: '#2563eb',
                          flexShrink: 0,
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {hasError && errorMessage && (
        <p
          style={{
            marginTop: '0.5rem',
            fontSize: '0.875rem',
            color: '#ef4444',
          }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

